import { Modal } from 'react-bootstrap';
import React, { FC, useEffect, useState, useMemo } from 'react';
import { KTSVG } from './../../../_metronic/helpers';
import * as API from './../../api';
import { PageTitle } from './../../../_metronic/layout/core';
import UserModal from './UserModal';
import MessageModal from './../utils/MessageModal';
import DeleteUserModal from './DeleteUserModal';
import BtnTrash from "../../components/BtnTrash";
import BtnEdit from "../../components/BtnEdit";
import BtnPermissions from "../../components/BtnPermissions";
import UserShieldIcon from "../../components/UserShieldIcon";
import PermissionsModal from './UserPermissionModal';
import { GetPermissions } from './../../modules/auth/core/Auth';
import { Search, X, Users, Shield, Mail, CheckCircle, XCircle } from 'lucide-react';

const UserList: FC = () => {
    const permissions = GetPermissions();
    const [selectedUser, setSelectedUser] = useState<any>();
    const [users, setUsers] = useState<any[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [modal, showModal] = useState<boolean>(false);
    const [messageModal, showMessageModal] = useState<boolean>(false);
    const [deleteModal, showDeleteModal] = useState<boolean>(false);
    const [permissionModal, showPermissionModal] = useState(false);
    const [groupPermissions, setGroupPermissions] = useState<any>([]);

    function onDeleteUserClick(user: any) {
        setSelectedUser(user);
        showDeleteModal(true);
    }

    function onEditUserClick(user: any) {
        setSelectedUser(user);
        showModal(true);
    }

    function onPermissionsClick(user: any) {
        setSelectedUser(user);
        showPermissionModal(true);
    }

    function onUserCreated() {
        updateData();
        showModal(false);
        setSelectedUser(null);
    }

    function onUserUpdated() {
        updateData();
        showModal(false);
        setSelectedUser(null);
    }

    async function updateData() {
        const response = await API.listUsers();
        const users = response.data.filter((user: any) => {
            return true;
        });
        users.forEach((user: any) => {
            try {
                user.permissions = JSON.parse(user.permissions).sort((a: number, b: number) => a - b);
            } catch (e) { }
        });
        setUsers(users);
    }

    async function loadPermissions() {
        const groupPermissions = await API.getGroupPermissions();
        setGroupPermissions(groupPermissions.data);
    }

    const getPermissionsName = (ids: any) => {
        try {
            if (!ids || !Array.isArray(ids) || ids.length === 0) return '-';
            if (!groupPermissions || !Array.isArray(groupPermissions)) return '-';

            const names = ids
                .map(id => {
                    const permission = groupPermissions.find(p => p.id_feature_group == id);
                    return permission ? permission.name : null;
                })
                .filter(name => name !== null);

            return names.join(', ');
        } catch (e) {
            console.log(e);
        }
        return '-';
    };

    const filteredUsers = useMemo(() => {
        if (!searchTerm.trim()) return users;

        const lowerSearch = searchTerm.toLowerCase();
        return users.filter(user => {
            const nameMatch = user.name?.toLowerCase().includes(lowerSearch);
            const emailMatch = user.email?.toLowerCase().includes(lowerSearch);
            const pipefyEmailMatch = user.email_pipefy?.toLowerCase().includes(lowerSearch);
            const permissionsMatch = getPermissionsName(user.permissions)?.toLowerCase().includes(lowerSearch);
            const roleMatch = (user.is_superadmin ? 'super admin' : user.is_admin ? 'administrador' : user.is_viewer ? 'visualizador' : 'membro').includes(lowerSearch);

            return nameMatch || emailMatch || pipefyEmailMatch || permissionsMatch || roleMatch;
        });
    }, [users, searchTerm, groupPermissions]);

    const getRoleBadge = (user: any) => {
        if (user.is_superadmin) {
            return (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                    <Shield className="w-3 h-3 mr-1" />
                    Super Admin
                </span>
            );
        }
        if (user.is_admin) {
            return (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
                    <Shield className="w-3 h-3 mr-1" />
                    Administrador
                </span>
            );
        }
        if (user.is_viewer) {
            return (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                    Visualizador
                </span>
            );
        }
        return (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                Membro
            </span>
        );
    };

    const getStatusBadge = (isActive: boolean) => {
        if (isActive) {
            return (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Ativo
                </span>
            );
        }
        return (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                <XCircle className="w-3 h-3 mr-1" />
                Desativado
            </span>
        );
    };

    const getAvatarStyle = (user: any) => {
        if (user.is_superadmin) return 'ring-4 ring-red-500 ring-offset-2';
        if (user.is_admin) return 'ring-4 ring-orange-500 ring-offset-2';
        return 'ring-2 ring-gray-200';
    };

    useEffect(() => {
        updateData();
        loadPermissions();
    }, []);

    return (
        <div className="min-h-screen bg-gray-50 ">
            <PageTitle>Administração de Usuários</PageTitle>

            <Modal id='modal_message' tabIndex={-1} dialogClassName='modal-dialog modal-dialog-centered mw-900px' show={messageModal}>
                <MessageModal
                    title='Função indisponível'
                    description='Já foi cadastrado o limite de usuários máximo para essa empresa. Caso seja necessário mais usuários, entre em contato com nossa equipe.'
                    onClose={() => showMessageModal(false)}
                />
            </Modal>

            <Modal id='kt_modal_create_app' tabIndex={-1} dialogClassName='modal-dialog modal-dialog-centered mw-900px' show={modal}>
                <UserModal
                    userData={selectedUser}
                    onUserUpdated={onUserUpdated}
                    onUserCreated={onUserCreated}
                    onClose={() => {
                        showModal(false);
                        setSelectedUser(null);
                    }}
                />
            </Modal>

            <Modal id='modal_message' tabIndex={-1} dialogClassName='modal-dialog modal-dialog-centered mw-600px' show={permissionModal}>
                <PermissionsModal
                    user={selectedUser}
                    permissions={groupPermissions}
                    onClose={() => {
                        showPermissionModal(false);
                        setSelectedUser(null);
                    }}
                    onChange={() => {
                        updateData();
                        showPermissionModal(false);
                        setSelectedUser(null);
                    }}
                />
            </Modal>

            <Modal id='kt_modal_delete_app' tabIndex={-1} dialogClassName='modal-dialog modal-dialog-centered mw-900px' show={deleteModal}>
                <DeleteUserModal
                    userData={selectedUser}
                    onClose={() => {
                        showDeleteModal(false);
                        setSelectedUser(null);
                    }}
                    onUserDeleted={() => {
                        showDeleteModal(false);
                        setSelectedUser(null);
                        updateData();
                    }}
                />
            </Modal>

            <div className="mx-auto ">
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200">
                    <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                                    <Users className="w-8 h-8 text-white" />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-white">Gerenciar Usuários</h2>
                                    <p className="text-blue-100 text-sm mt-1">
                                        {filteredUsers.length} {filteredUsers.length === 1 ? 'usuário encontrado' : 'usuários encontrados'}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => showModal(true)}
                                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-blue-600 rounded-xl font-semibold hover:bg-blue-50 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                            >
                                <KTSVG path='media/icons/duotune/arrows/arr075.svg' className='w-5 h-5' />
                                Adicionar Usuário
                            </button>
                        </div>
                    </div>

                    <div className="p-8">
                        <div className="mb-6">
                            <div className="relative">
                                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    type="text"
                                    placeholder="Buscar por nome, e-mail, cargo ou permissões..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-12 pr-12 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-gray-700 placeholder-gray-400"
                                />
                                {searchTerm && (
                                    <button
                                        onClick={() => setSearchTerm('')}
                                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                )}
                            </div>
                        </div>

                        {filteredUsers.length === 0 ? (
                            <div className="text-center py-16">
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                                    <Users className="w-8 h-8 text-gray-400" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum usuário encontrado</h3>
                                <p className="text-gray-500">
                                    {searchTerm ? 'Tente ajustar sua busca' : 'Adicione um novo usuário para começar'}
                                </p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b-2 border-gray-200">
                                            <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                Usuário
                                            </th>
                                            <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                Contato
                                            </th>
                                            <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                Status
                                            </th>
                                            <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                Permissões
                                            </th>
                                            <th className="text-right py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                Ações
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {filteredUsers.map((user: any) => (
                                            <tr
                                                key={user.guid}
                                                className="hover:bg-gray-50 transition-colors duration-150"
                                            >
                                                <td className="py-5 px-4">
                                                    <div className="flex items-center gap-4">
                                                        <div className={`relative w-12 h-12 rounded-full overflow-hidden flex-shrink-0 ${getAvatarStyle(user)}`}>
                                                            {user.src ? (
                                                                <img
                                                                    src={user.src}
                                                                    alt={user.name}
                                                                    className="w-full h-full object-cover"
                                                                />
                                                            ) : (
                                                                <div className={`w-full h-full flex items-center justify-center ${user.is_superadmin ? 'bg-red-100 text-red-600' : user.is_admin ? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-blue-600'
                                                                    }`}>
                                                                    <UserShieldIcon />
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="min-w-0 flex-1">
                                                            <p className="text-sm font-semibold text-gray-900 truncate">
                                                                {user.name}
                                                            </p>
                                                            <div className="mt-1">
                                                                {getRoleBadge(user)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="py-5 px-4">
                                                    <div className="space-y-1">
                                                        <div className="flex items-center gap-2 text-sm text-gray-600">
                                                            <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                            <span className="truncate">{user.email}</span>
                                                        </div>
                                                        {user.email_pipefy && (
                                                            <div className="pl-6 text-xs text-gray-500 truncate" title="E-mail usado no Pipefy">
                                                                Pipefy: {user.email_pipefy}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="py-5 px-4">
                                                    {getStatusBadge(user.is_actived)}
                                                </td>
                                                <td className="py-5 px-4">
                                                    <p className="text-sm text-gray-600 line-clamp-2">
                                                        {getPermissionsName(user.permissions)}
                                                    </p>
                                                </td>
                                                <td className="py-5 px-4">
                                                    <div className="flex items-center justify-end gap-2">
                                                        {permissions?.some(feature => feature.name.includes('users-permissions')) && (
                                                            <button
                                                                onClick={() => onPermissionsClick(user)}
                                                                className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-all duration-200 transform hover:scale-110"
                                                                title="Gerenciar Permissões"
                                                            >
                                                                <BtnPermissions />
                                                            </button>
                                                        )}
                                                        {permissions?.some(feature => feature.name.includes('users-edit')) && (
                                                            <button
                                                                onClick={() => onEditUserClick(user)}
                                                                className="p-2 bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 transition-all duration-200 transform hover:scale-110"
                                                                title="Editar Usuário"
                                                            >
                                                                <BtnEdit />
                                                            </button>
                                                        )}
                                                        {permissions?.some(feature => feature.name.includes('users-remove')) && (
                                                            <button
                                                                onClick={() => onDeleteUserClick(user)}
                                                                className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-all duration-200 transform hover:scale-110"
                                                                title="Remover Usuário"
                                                            >
                                                                <BtnTrash />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserList;
